import React from 'react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import throttle from 'lodash/throttle';
import { BASE_HEADER_HEIGHT } from '@/constants/profile-builder';
import { useHasBeenSeen, useClintMediaQuery } from '@/utils/hooks';
import { trackClickEvent } from '@/utils/site-track';

const GENERAL_TAB_DISTANCE = -8; //GENERAL_MARGIN -  BASE_HEADER_HEIGHT

interface Props {
  generalEl: React.RefObject<any>;
  tabsContent: {
    key: string;
    id: string;
    name: string;
    node?: React.ReactNode;
  }[];
  tabMargin: number;
  tabsBottomMargin: number;
  className?: string;
  tabsClassName?: string;
  tabClassName?: string;
  offsetPixels?: number; // The difference in distance between header and main
  dataEleGroup?: string;
  stickyTopClassName?: string;
}

const MyTabs = (props: Props) => {
  const {
    generalEl,
    tabsContent,
    tabMargin,
    tabsBottomMargin,
    className = '',
    tabsClassName = '',
    tabClassName = '',
    offsetPixels = 0,
    dataEleGroup,
    stickyTopClassName = 'top-16',
  } = props;
  const isMobile = useClintMediaQuery();
  const [active, setActive] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('');
  const tabsEl = React.useRef<any>(null);
  const tabKeys = tabsContent.map(({ key }) => key);

  const getTabTopHeight = (index: number) => {
    let topHeight = 0;
    tabsContent.slice(0, index).map((item) => {
      const height = document
        .getElementById(item.id)
        ?.getBoundingClientRect().height;
      if (height) topHeight += height;
    });
    return topHeight;
  };

  const tabMove = (tab: string) => {
    const generalHeight =
      generalEl?.current?.getBoundingClientRect().height || 0;
    const generalMargin =
      window.getComputedStyle(generalEl?.current).marginBottom || '0';

    const index = tabsContent.findIndex((item) => item.key === tab);
    if (index !== -1) {
      const topHeight = getTabTopHeight(index);
      const totalTop =
        generalHeight +
        parseInt(generalMargin) +
        topHeight +
        offsetPixels +
        index * tabMargin;
      if (document.scrollingElement && totalTop)
        document.scrollingElement.scrollTo({
          top: totalTop,
        });
    }
  };

  const replaceUrlHash = (tab = '') => {
    const hash = window.location.hash.replace(/(.*)#/, '');
    if (hash === tab) {
      return;
    }
    setActiveTab(tab);
    const url = `${window.location.href.replace(/#(.*)/, '')}${tab ? `#${tab}` : ''}`;
    window.history.pushState(null, '', url);
  };

  const handleTabChange = (e: React.SyntheticEvent, tab: string) => {
    tabMove(tab);
    replaceUrlHash(tab);
    trackClickEvent(e);
  };

  const tabScroll = React.useCallback(
    throttle(() => {
      const tabHeight = tabsEl?.current?.getBoundingClientRect().height || 0;
      for (const index in tabsContent) {
        const id = tabsContent[index].id;
        const tab = tabsContent[index].key;
        const baseMargin = index === '0' ? tabsBottomMargin : tabMargin;
        const baseBottom = BASE_HEADER_HEIGHT + offsetPixels + tabHeight;
        const baseTop = baseBottom + baseMargin;
        const top = Math.floor(
          document.getElementById(id)?.getBoundingClientRect().top || 0,
        );
        const height = document
          .getElementById(id)
          ?.getBoundingClientRect().height;
        if (index === '0' && top !== undefined && top > baseTop) {
          replaceUrlHash();
          break;
        }
        if (top !== undefined && height !== undefined && top <= baseTop) {
          if (top > parseInt((baseBottom - height).toString())) {
            replaceUrlHash(tab);
            break;
          } else if (index === `${tabsContent.length - 1}`) {
            replaceUrlHash();
            break;
          }
        }
      }
    }, 300),
    [],
  );

  useHasBeenSeen(
    generalEl,
    () => setActive(false),
    () => setActive(true),
    GENERAL_TAB_DISTANCE,
  );

  React.useEffect(() => {
    const nextTab = window.location.hash.replace(/(.*)#/, '');
    if (nextTab) {
      if (tabKeys.includes(nextTab)) {
        setActiveTab(nextTab);
        tabMove(nextTab);
      }
    } else {
      tabScroll();
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('scroll', tabScroll);
    return () => window.removeEventListener('scroll', tabScroll);
  }, []);

  return (
    <div
      data-active={active}
      data-mobile={isMobile}
      className={`sticky z-20 ${stickyTopClassName} overflow-hidden ${className}`}
    >
      <Tabs
        ref={tabsEl}
        value={activeTab || false}
        className={tabsClassName}
        onChange={handleTabChange}
      >
        {tabsContent.map(({ key, name }) => {
          return (
            <Tab
              key={key}
              value={key}
              className={tabClassName}
              data-active={activeTab === key}
              label={name}
              data-ele-id={`${key}_${name}`}
              data-ele-group={dataEleGroup}
            />
          );
        })}
      </Tabs>
    </div>
  );
};

export default MyTabs;
